<template>
    <div class="reset-page">

        <Panel>
            <div class="reset-form login-form">
                <div v-if="isLoading" class="text-center">
                    <Loader wait-text="Processing..."/>
                </div>

                <div v-if="isSuccessfulReset">
                    <Alert
                        type="success"
                        message="If the email matched our records, you will receive a password reset link on your email."
                    />

                    <a href="/">Go to Login</a>
                </div>

                <div v-if="!isLoading && !isSuccessfulReset">
                    <h1>Reset your password</h1>

                    <Alert type="info"
                           v-if="!isAzureAdUser"
                           message="Please enter your e-mail you used to log in. If your email matches our records we will send you an email to confirm password reset."/>

                    <Alert type="danger" v-if="errorMessage.length > 0" :message="errorMessage"/>

                    <form @submit.prevent="handleResetRequest">
                        <div class="form-group">
                            <label for="email">E-mail</label>
                            <input id="email" type="text" v-model="form.email" @keydown="doResetIfEnter" placeholder="E-mail"
                                   required class="form-control">
                        </div>

                        <div class="text-right reset-buttons">
                            <button type="submit" class="btn btn-primary" :disabled="!isFormValid">
                                <span v-if="isAzureAdUser">
                                    <i class="fa fa-link"></i>
                                    Go to EMS|MC Password Reset
                                </span>
                                <span v-else>
                                    <i class="fa fa-envelope"></i>
                                    Reset password
                                </span>

                            </button>
                            <button type="button" class="btn btn-outline-danger" @click="handleCancel">Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        </Panel>

    </div>
</template>

<script>
import Alert from "@/components/Alert.vue";
import Panel from "@/components/Panel";
import {useLoading} from "@/hooks/use-loading";
import {computed, ref} from "vue";
import {useRouter} from "vue-router";
import Loader from "@/components/Loader";
import {goToAzureAdPasswordReset, isAzureAdEmail} from "@/lib/azure";
import {useForm} from "@/hooks/use-form";
import {required, email} from "vuelidate/lib/validators";
import {requestPasswordReset} from "@/api/user";

export default {
    name: "ResetPasswordPage",
    components: {
        Loader,
        Panel,
        Alert,
    },
    setup() {
        const {isLoading, startLoading, stopLoading} = useLoading(false);

        const errorMessage = ref('');
        const isSuccessfulReset = ref(false);

        const {v$, form} = useForm({
            props: {
                email: ''
            },
            rules: {
                email: { required, email }
            }
        });

        const isFormValid = computed(() => !v$.value.$invalid);
        const isAzureAdUser = computed(() => isAzureAdEmail(form.email));

        const router = useRouter();

        const handleResetRequest = async () => {
            if (isAzureAdUser.value) {
                goToAzureAdPasswordReset();
                return;
            }

            if (v$.value.$invalid) {
                return;
            }

            startLoading();

            try {
                await requestPasswordReset({
                    email: form.email
                });

                isSuccessfulReset.value = true;
            } catch (e) {
                errorMessage.value = e?.response?.data?.message ?? e.message;
            } finally {
                stopLoading();
            }
        };

        const handleCancel = () => {
            router.push('/login');
        };

        return {
            form,
            isSuccessfulReset,
            errorMessage,
            isLoading,
            isFormValid,
            isAzureAdUser,
            handleResetRequest,
            handleCancel,
        };
    }
}
</script>

<style scoped lang="scss">
.reset-page {
    max-width: 500px;
    margin: 10% auto 0 auto;

    h1 {
        font-size: 20px;
        padding-bottom: 20px;
    }

    .reset-buttons {
        button {
            display: inline-block;
            margin-left: 15px;
        }
    }

}
</style>